import { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { ProductDetailLogicReturn } from './ProductDetail.types';
import { ComponentData, ComponentRenderMode } from '../../../types/component-skin';
import { 
  fetchProductDetail, 
  selectProductDetailState, 
  resetProductDetailState, 
  addToCart, 
  selectIsUserLoggedIn, 
  selectUser, 
  selectWithcookieData 
} from '../../../redux/WithcookieSlice';
import { useTranslation } from '../../../redux/TranslationHelper';
import { showToast } from '../../common/Toast';

/**
 * 상품 상세 컴포넌트 로직 훅
 */
const useProductDetailLogic = (componentData: ComponentData, mode: ComponentRenderMode): ProductDetailLogicReturn => {
  // 호환성을 위한 props 변환
  const props = { ...componentData.props, ...componentData.componentProps, ...componentData };
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { id: urlProductId } = useParams<{ id: string }>();
  
  // Redux 상태
  const { productData, loading, error } = useSelector(selectProductDetailState);
  const isUserLoggedIn = useSelector(selectIsUserLoggedIn);
  const userInfo = useSelector(selectUser);
  const withcookieData = useSelector(selectWithcookieData);
  
  // URL 경로 변수에서 productId를 우선적으로 가져오고, 없으면 componentProps의 값 사용
  const productId = urlProductId 
    ? parseInt(urlProductId, 10) 
    : (componentData.pathVariables?.id 
      ? parseInt(componentData.pathVariables.id, 10) 
      : props.productId || props.componentProps?.productId || 0);
  
  // 상태 관리
  const [quantity, setQuantity] = useState(1);
  const [selectedImage, setSelectedImage] = useState<string>('');
  const [selectedOptions, setSelectedOptions] = useState<Record<number, number>>({});
  const [selectedVariant, setSelectedVariant] = useState<any>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [showBottomSheet, setShowBottomSheet] = useState(false);
  const [activeTab, setActiveTab] = useState('description');
  
  // API 호출 중복 방지를 위한 ref
  const isFetchingRef = useRef(false);
  const lastFetchedProductIdRef = useRef<number | null>(null);
  
  // 관리자 모드 확인
  const isAdminMode = mode === 'editor' || props.mode === 'admin' || props.isPreview;
  
  // 모바일 감지
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    handleResize();
    window.addEventListener('resize', handleResize);
    
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);
  
  // API에서 상품 데이터 가져오기
  useEffect(() => {
    // productId가 없거나 이미 가져온 상품이면 스킵
    if (!productId || lastFetchedProductIdRef.current === productId) {
      return;
    }
    
    // 이미 API 호출 중이면 스킵
    if (isFetchingRef.current) {
      return;
    }
    
    isFetchingRef.current = true;
    lastFetchedProductIdRef.current = productId;
    
    dispatch(fetchProductDetail(productId) as any)
      .finally(() => {
        isFetchingRef.current = false;
      });
  }, [dispatch, productId]);
  
  // productId가 변경될 때마다 상태 초기화
  useEffect(() => {
    // Redux 상태 초기화
    dispatch(resetProductDetailState());
    // 컴포넌트 로컬 상태 초기화
    setSelectedImage('');
    setSelectedOptions({});
    setSelectedVariant(null);
    setQuantity(1);
    setShowBottomSheet(false);
  }, [productId, dispatch]);
  
  // 타입 안전성 보장을 위한 상품 데이터 처리 - 객체 복사 후 수정
  const productFromStore = productData?.product || productData as any;
  
  // 상품 데이터가 있으면 복사본 생성 후 PV 값 설정
  let product = productFromStore;
  if (productFromStore) {
    // 객체를 복사하여 수정 가능하게 만듦
    product = { ...productFromStore };
    
    // 기본 PV 값 매핑 - config.generate_pv에서 가져오기
    product.pv = product.config?.generate_pv || 0;
    
    // 등급/직급별 PV 적용 (priority와 무관하게 실제 데이터 기준)
    if (product.optionJson && userInfo && isUserLoggedIn) {
      // level1 체크 (사용자가 level1을 가지고 있고, 상품에 level1 데이터가 있는 경우)
      if (userInfo.level1 && product.optionJson.level1_pv) {
        const foundPV = product.optionJson.level1_pv[userInfo.level1];
        if (foundPV !== undefined && foundPV !== null) {
          product.pv = Number(foundPV);
        }
      }
      // level2 체크 (사용자가 level2를 가지고 있고, 상품에 level2 데이터가 있는 경우)
      else if (userInfo.level2 && product.optionJson.level2_pv) {
        const foundPV = product.optionJson.level2_pv[userInfo.level2];
        if (foundPV !== undefined && foundPV !== null) {
          product.pv = Number(foundPV);
        }
      }
    }
  }
  
  // 메인 이미지와 추가 이미지들을 하나의 배열로 결합
  const allImages = [
    product?.config?.img_url || product?.config?.main_image || product?.mainImage,
    ...(product?.additionalImages || [])
  ].filter(Boolean);
  
  // 상품 데이터가 로드되면 첫 번째 이미지를 선택
  useEffect(() => {
    if (allImages.length > 0 && !selectedImage) {
      setSelectedImage(allImages[0]);
    }
  }, [product?.id, allImages.length]); // eslint-disable-line react-hooks/exhaustive-deps
  
  // 선택된 옵션에 따라 variant 찾기
  useEffect(() => {
    if (!product?.variants || !product?.optionGroups) return;
    
    // 모든 필수 옵션이 선택되었는지 확인
    const requiredGroups = product.optionGroups.filter((g: any) => g.isRequired);
    const allRequiredSelected = requiredGroups.every((g: any) => selectedOptions[g.id]);
    
    if (!allRequiredSelected) {
      setSelectedVariant(null);
      return;
    }
    
    // 선택된 옵션들과 일치하는 variant 찾기
    const matchingVariant = product.variants.find((variant: any) => {
      if (!variant.options || variant.options.length === 0) return false;
      
      return variant.options.every((variantOption: any) => {
        const groupId = variantOption.optionValue.optionGroupId;
        return selectedOptions[groupId] === variantOption.optionValueId;
      });
    });
    
    setSelectedVariant(matchingVariant || null);
  }, [selectedOptions, product?.variants, product?.optionGroups]);
  
  // 옵션 선택 핸들러
  const handleOptionSelect = useCallback((groupId: number, valueId: number) => {
    setSelectedOptions(prev => ({
      ...prev,
      [groupId]: valueId
    }));
  }, []);
  
  // 이미지 변경 핸들러
  const handleImageChange = useCallback((image: string) => {
    setSelectedImage(image);
  }, []);
  
  // 수량 변경 핸들러
  const handleQuantityChange = useCallback((newQuantity: number) => {
    if (newQuantity < 1) return;
    if (product?.stock && newQuantity > product.stock) {
      showToast(t('재고가 부족합니다.'), 'error');
      return;
    }
    setQuantity(newQuantity);
  }, [product?.stock, t]);
  
  // 탭 변경 핸들러
  const handleTabChange = useCallback((tab: string) => {
    setActiveTab(tab);
  }, []);
  
  // 수량 증가
  const increaseQuantity = useCallback(() => {
    handleQuantityChange(quantity + 1);
  }, [quantity, handleQuantityChange]);
  
  // 수량 감소
  const decreaseQuantity = useCallback(() => {
    handleQuantityChange(quantity - 1);
  }, [quantity, handleQuantityChange]);
  
  // 선택한 옵션 ID를 코드로 변환하는 함수
  const getOptionsForCart = useCallback(() => {
    const options: Record<string, string> = {};
    
    if (!product?.optionGroups || !selectedVariant) return options;
    
    product.optionGroups.forEach((group: any) => {
      const selectedValueId = selectedOptions[group.id];
      if (selectedValueId) {
        const selectedValue = group.optionValues?.find((v: any) => v.id === selectedValueId);
        if (selectedValue) {
          options[group.code] = selectedValue.code;
        }
      }
    });
    
    return options;
  }, [product?.optionGroups, selectedOptions, selectedVariant]);
  
  // 등급/직급별 가격 계산 함수 (optionJson priority 기반)
  const calculateLevelPrice = useCallback((basePrice: number) => {
    // 로그인하지 않은 경우 또는 optionJson이 없는 경우
    if (!userInfo || !isUserLoggedIn || !product?.optionJson?.priority) {
      return {
        originalPrice: basePrice,
        levelPrice: basePrice,
        discount: 0,
        discountRate: 0,
        levelName: null
      };
    }
    
    const priority = product.optionJson.priority;
    let levelPrice = basePrice;
    let levelName = null;
    let levelId: string | number | null = null;
    let levelPolicy = null;
    
    // priority에 따라 level1 또는 level2 사용
    if (priority === 'level1' && userInfo.level1) {
      levelId = userInfo.level1;
      levelPolicy = userInfo.level1Policy;
      // 등급 이름 가져오기
      const level1Name = levelPolicy?.name || levelPolicy?.customName || levelPolicy?.type || '';
      levelName = level1Name ? `${level1Name} 할인` : '등급 할인';
      
      // level1_price 확인
      if (product.optionJson.level1_price && levelId !== null && product.optionJson.level1_price[levelId]) {
        levelPrice = Number(product.optionJson.level1_price[levelId]) || basePrice;
      }
    } else if (priority === 'level2' && userInfo.level2) {
      levelId = userInfo.level2;
      levelPolicy = userInfo.level2Policy;
      // 직급 이름 가져오기
      const level2Name = levelPolicy?.name || levelPolicy?.customName || levelPolicy?.type || '';
      levelName = level2Name ? `${level2Name} 할인` : '직급 할인';
      
      // level2_price 확인
      if (product.optionJson.level2_price && levelId !== null && product.optionJson.level2_price[levelId]) {
        levelPrice = Number(product.optionJson.level2_price[levelId]) || basePrice;
      }
    }
    
    // 개별 가격이 없으면 supply_price_ratio 사용
    if (levelPrice === basePrice && levelPolicy?.settingJson?.supply_price_ratio) {
      const supplyPriceRatio = Number(levelPolicy.settingJson.supply_price_ratio) || 1;
      levelPrice = Math.floor(basePrice * supplyPriceRatio);
    } else if (levelPrice === basePrice && levelId) {
      // withcookieData에서 정책 찾기
      const levelPolicies = withcookieData?.skin?.company?.levelpolicies || withcookieData?.company?.levelpolicies || [];
      const matchingPolicy = levelPolicies.find((policy: any) => policy.id === levelId);
      if (matchingPolicy?.settingJson?.supply_price_ratio) {
        const supplyPriceRatio = Number(matchingPolicy.settingJson.supply_price_ratio) || 1;
        levelPrice = Math.floor(basePrice * supplyPriceRatio);
        if (!levelName && matchingPolicy) {
          levelName = matchingPolicy.customName || matchingPolicy.type || (priority === 'level1' ? '등급 할인' : '직급 할인');
        }
      }
    }
    
    const discount = basePrice - levelPrice;
    const discountRate = basePrice > 0 ? Math.round((discount / basePrice) * 100) : 0;
    
    return {
      originalPrice: basePrice,
      levelPrice,
      discount,
      discountRate,
      levelName: levelId ? levelName : null
    };
  }, [userInfo, isUserLoggedIn, product?.optionJson, withcookieData]);
  
  // 장바구니 추가 함수
  const handleAddToCart = useCallback(async () => {
    if (!productData || !productId) {
      showToast(t('상품 정보가 유효하지 않습니다.'), 'error');
      return;
    }
    
    try {
      // 1. Variant가 있는 경우
      if (product?.variants && product.variants.length > 0) {
        // 옵션 그룹이 있고 필수 옵션이 있는 경우
        if (product?.optionGroups && product.optionGroups.length > 0) {
          const requiredGroups = product.optionGroups.filter((g: any) => g.isRequired);
          const allRequiredSelected = requiredGroups.every((g: any) => selectedOptions[g.id]);
          
          if (!allRequiredSelected) {
            showToast(t('필수 옵션을 선택해주세요.'), 'error');
            return;
          }
          
          if (!selectedVariant) {
            showToast(t('상품 옵션을 선택해주세요.'), 'error');
            return;
          }
          
          // variant 재고 확인
          if (selectedVariant.stock === 0) {
            showToast(t('품절된 상품입니다.'), 'error');
            return;
          }
        }
        
        // Variant가 있는 상품 - variant_id와 options 포함
        const cartData = {
          product_id: productId,
          variant_id: selectedVariant?.id,
          count: quantity,
          options: getOptionsForCart()
        };
        
        // 먼저 사용자에게 확인
        if (!window.confirm(t('장바구니에 추가하시겠습니까?'))) {
          return;
        }
        
        await dispatch(addToCart(cartData) as any).unwrap();
        showToast(t('상품이 장바구니에 추가되었습니다.') + ` (${quantity}` + t('개') + `)`, 'success');
        
        // 모바일에서는 bottomSheet 닫기
        if (isMobile) {
          setShowBottomSheet(false);
        }
        
        // 옵션: 장바구니 페이지로 이동하거나 현재 페이지에 남을 수 있도록 확인
        if (window.confirm(t('장바구니 페이지로 이동하시겠습니까?'))) {
          if (componentData.utils?.navigate) {
            componentData.utils.navigate('/cart');
          } else {
            window.location.href = '/cart';
          }
        }
      } 
      // 2. Variant가 없는 경우 - 단순 상품으로 처리
      else {
        // 기본 재고 확인
        if (product?.config?.stock_count === 0) {
          showToast(t('품절된 상품입니다.'), 'error');
          return;
        }
        
        // Variant 없이 장바구니 추가
        const cartData = {
          product_id: productId,
          count: quantity
        };
        
        // 먼저 사용자에게 확인
        if (!window.confirm(t('장바구니에 추가하시겠습니까?'))) {
          return;
        }
        
        await dispatch(addToCart(cartData) as any).unwrap();
        showToast(t('상품이 장바구니에 추가되었습니다.') + ` (${quantity}` + t('개') + `)`, 'success');
        
        // 모바일에서는 bottomSheet 닫기
        if (isMobile) {
          setShowBottomSheet(false);
        }
        
        // 옵션: 장바구니 페이지로 이동하거나 현재 페이지에 남을 수 있도록 확인
        if (window.confirm(t('장바구니 페이지로 이동하시겠습니까?'))) {
          if (componentData.utils?.navigate) {
            componentData.utils.navigate('/cart');
          } else {
            window.location.href = '/cart';
          }
        }
      }
    } catch (error: any) {
      console.error('장바구니 추가 오류:', error);
      
      // 로그인이 필요한 경우
      if (error.code === 'AUTH_REQUIRED') {
        if (window.confirm(t('로그인이 필요한 서비스입니다. 로그인 페이지로 이동하시겠습니까?'))) {
          if (componentData.utils?.navigate) {
            componentData.utils.navigate('/login');
          } else {
            window.location.href = '/login';
          }
        }
      } else {
        // 기타 오류
        showToast(t('장바구니 추가 실패') + ': ' + (error.message || error), 'error');
      }
    }
  }, [productData, productId, product, selectedVariant, selectedOptions, quantity, t, dispatch, isMobile, getOptionsForCart, componentData.utils]);
  
  // 바로 구매 핸들러
  const handleBuyNow = useCallback(async () => {
    // 장바구니에 추가 후 결제 페이지로 이동
    try {
      await handleAddToCart();
      // 장바구니 추가가 성공하면 결제 페이지로 이동
      if (componentData.utils?.navigate) {
        componentData.utils.navigate('/payment');
      } else {
        window.location.href = '/payment';
      }
    } catch (error) {
      // 에러는 handleAddToCart에서 처리됨
    }
  }, [handleAddToCart, componentData.utils]);
  
  // 기본 가격 계산
  const basePrice = Number(product?.config?.discounted_price) || 
                    Number(product?.config?.default_price) || 
                    Number(product?.price) || 0;
  
  // 등급별 가격 계산
  const priceInfo = calculateLevelPrice(basePrice);
  
  // 최종 가격 (등급 할인 + variant 추가금액)
  const finalPrice = priceInfo.levelPrice + (selectedVariant ? (Number(selectedVariant.additionalPrice) || 0) : 0);
  
  // 재고 상태
  const isOutOfStock = (product?.variants?.length > 0 && selectedVariant && selectedVariant.stock === 0) ||
                       (!product?.variants?.length && product?.config?.stock_count === 0);
  
  return {
    data: {
      product,
      selectedOptions,
      quantity,
      loading,
      error,
      activeTab,
      mainImage: selectedImage || allImages[0] || '/images/product-placeholder.png',
      allImages,
      relatedProducts: productData?.relatedProducts || [],
      selectedVariant,
      isMobile,
      showBottomSheet,
      isUserLoggedIn,
      isAdminMode,
      theme: withcookieData?.theme || {},
      // 가격 정보
      basePrice,
      priceInfo,
      finalPrice,
      isOutOfStock
    },
    actions: {
      handleOptionChange: handleOptionSelect,
      handleQuantityChange,
      handleAddToCart,
      handleBuyNow,
      handleTabChange,
      handleImageChange,
      increaseQuantity,
      decreaseQuantity,
      setShowBottomSheet
    }
  };
};

export default useProductDetailLogic;